import { faBirthdayCake, faEllipsisH, faEnvelope } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { TableCell, TableRow, Tooltip, Typography, Grid } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { isAfter, isBefore, isSameDay } from "date-fns";
import React, { useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ApproverAddress, ApproversContainer, EmployeeNameBlock } from "./styles";
import { useAppSelector } from "../../../custom-hooks/redux/redux";
import { isAuthorizedEeGroup } from "../../../custom-hooks/useAuthorization/useAuthorization";
import { CustomDate } from "../../../models/custom-date/custom-date";
import { EmployeeApproversDTO } from "../../../models/employee-approver/employee-approver-dto";
import { EmployeeGroupRef } from "../../../models/employee-groups";
import { EmployeeProjectAssignmentUserRef } from "../../../models/employee-project-assignment";
import { EmployeeRef } from "../../../models/employees";
import { WithEmployee } from "../../../models/employees/state-employee";
import ConcatenatedCell from "../../../reusable-components/ConcatenatedCell/ConcatenatedCell";
import SimplePopover from "../../../reusable-components/SimplePopover/SimplePopover";
import { AUTHORIZATIONS } from "../../../utils/constants/authorization-constants";
import { intlFormatDateShort } from "../../../utils/time-utils";
import { openTimesheetViewMode } from "../../../views/my-t-and-a/my-ee-tna-utils";

interface Props {
  item: EmployeeApproversDTO;
  onlyCurrentClientApprover: boolean;
}
const TnAApproverRow = ({ item, onlyCurrentClientApprover }: Props) => {
  const theme = useTheme();

  const navigate = useNavigate();

  const { currentUserAuthorizations } = useAppSelector((state) => state.currentUserReducer);

  const navigateToTimesheet = (employee: EmployeeRef | null) => {
    const canApproveReject = allowedToApproveReject(
      employee?.groups?.map((group: EmployeeGroupRef) => group?.id) ?? []
    );

    const canViewOnly = hasAccessToViewOnly(employee?.groups?.map((group: EmployeeGroupRef) => group?.id) ?? []);

    if (!canApproveReject && canViewOnly && employee?.id) {
      openTimesheetViewMode(employee.id);
    }

    if (canApproveReject && employee?.id) {
      navigate("/my-t-and-a", {
        state: {
          employee,
          forManager: true,
        } as WithEmployee,
      });
    }
  };

  const allowedToApproveReject = useCallback(
    (groupIds: string[]) => {
      return isAuthorizedEeGroup(
        [AUTHORIZATIONS.EmployeesAccess.ApproveTimeAndLeaves],
        currentUserAuthorizations,
        groupIds
      );
    },
    [currentUserAuthorizations]
  );

  const hasAccessToViewOnly = useCallback(
    (groupIds: string[]) => {
      return isAuthorizedEeGroup(
        [AUTHORIZATIONS.EmployeesAccess.ViewNonSalaryAndCompensationData],
        currentUserAuthorizations,
        groupIds
      );
    },
    [currentUserAuthorizations]
  );

  const isCurrentApprover = (fromDate: Date | null, endDate: Date | null) => {
    if (
      fromDate &&
      (isBefore(new Date(fromDate), new Date()) || isSameDay(new Date(fromDate), new Date())) &&
      (!endDate || (endDate && (isAfter(new Date(endDate), new Date()) || isSameDay(new Date(endDate), new Date()))))
    ) {
      return true;
    } else {
      return false;
    }
  };

  const filterclientApprover = (approvers: EmployeeProjectAssignmentUserRef[]) => {
    if (!onlyCurrentClientApprover) {
      return approvers;
    } else {
      return approvers.filter((approver) => isCurrentApprover(approver.startDate, approver.endDate));
    }
  };
  return (
    <>
      <TableRow>
        {/* Employee code */}
        <TableCell rowSpan={item.projectClientApprovers.length ? item.projectClientApprovers.length : 1}>
          {
            <ConcatenatedCell
              aligned="center"
              FirstElement={
                <Tooltip title="">
                  <Typography>{item.employee?.employeeCode}</Typography>
                </Tooltip>
              }
              SecondElement={
                <Tooltip title="">
                  <Typography>{item.employee?.internalId}</Typography>
                </Tooltip>
              }
            />
          }
        </TableCell>
        {/* End of employee code */}
        {/* Employee name */}
        <TableCell rowSpan={item.projectClientApprovers.length ? item.projectClientApprovers.length : 1}>
          <ConcatenatedCell
            aligned="left"
            FirstElement={
              <EmployeeNameBlock
                onClick={() => {
                  navigateToTimesheet(item.employee);
                }}
              >
                <Typography>{item.employee?.name}</Typography>
                <Typography variant="body2">
                  <FontAwesomeIcon icon={faEnvelope} />
                  &nbsp;
                  {item?.employee?.email}
                </Typography>

                {item?.employee?.birthDate && (
                  <Typography variant={"body2"}>
                    <FontAwesomeIcon
                      icon={faBirthdayCake}
                      size="xs"
                    />{" "}
                    &nbsp;
                    {intlFormatDateShort(item.employee.birthDate)}
                  </Typography>
                )}
              </EmployeeNameBlock>
            }
            SecondElement={<Typography>{item.employee?.legalEntity?.code}</Typography>}
          />
        </TableCell>
        {/* End of employee name */}
        {/* TnA Approvers */}
        <TableCell rowSpan={item.projectClientApprovers.length ? item.projectClientApprovers.length : 1}>
          <ApproversContainer>
            {item.managerApproversTimesheet?.slice(0, 3).map((approver, idx) => (
              <Tooltip
                key={idx}
                title={`${approver.name} : ${approver.email}`}
              >
                <Grid>
                  <Typography>{approver.name}</Typography>
                  {/* <a href={`mailto:${approver.email}`}>
                    <ApproverAddress variant="body2">
                      <FontAwesomeIcon icon={faEnvelope} />
                      &nbsp;
                      {approver.email}
                    </ApproverAddress>
                  </a> */}
                </Grid>
              </Tooltip>
            ))}
          </ApproversContainer>
          {item.managerApproversTimesheet.length > 3 && (
            <Grid
              style={{
                marginLeft: "auto",
                width: "min-content",
              }}
            >
              <SimplePopover
                buttonType="icon"
                buttonContent={<FontAwesomeIcon icon={faEllipsisH} />}
              >
                <ApproversContainer style={{ padding: "15px" }}>
                  {item.managerApproversTimesheet?.slice(3).map((approver, idx) => (
                    <Tooltip
                      key={idx}
                      title={`${approver.name} : ${approver.email}`}
                    >
                      <Grid>
                        <Typography>{approver.name}</Typography>
                        {/* <a href={`mailto:${approver.email}`}>
                          <ApproverAddress variant="body2">
                            <FontAwesomeIcon icon={faEnvelope} />
                            &nbsp;
                            {approver.email}
                          </ApproverAddress>
                        </a> */}
                      </Grid>
                    </Tooltip>
                  ))}
                </ApproversContainer>
              </SimplePopover>
            </Grid>
          )}
        </TableCell>
        {/* End TnA Approvers */}
        {/* Expense report Approvers */}
        <TableCell rowSpan={item.projectClientApprovers.length ? item.projectClientApprovers.length : 1}>
          <ApproversContainer>
            {item.managerApproversExpenses?.slice(0, 3).map((approver, idx) => (
              <Tooltip
                key={idx}
                title={`${approver.name} : ${approver.email}`}
              >
                <Grid>
                  <Typography>{approver.name}</Typography>
                  {/* <a href={`mailto:${approver.email}`}>
                    <ApproverAddress variant="body2">
                      <FontAwesomeIcon icon={faEnvelope} />
                      &nbsp;
                      {approver.email}
                    </ApproverAddress>
                  </a> */}
                </Grid>
              </Tooltip>
            ))}
            {item.managerApproversExpenses.length > 3 && (
              <Grid
                style={{
                  marginLeft: "auto",
                  width: "min-content",
                }}
              >
                <SimplePopover
                  buttonType="icon"
                  buttonContent={<FontAwesomeIcon icon={faEllipsisH} />}
                >
                  <ApproversContainer style={{ padding: "15px" }}>
                    {item.managerApproversExpenses?.slice(3).map((approver, idx) => (
                      <Tooltip
                        key={idx}
                        title={`${approver.name} : ${approver.email}`}
                      >
                        <Grid>
                          <Typography>{approver.name}</Typography>
                          {/* <a href={`mailto:${approver.email}`}>
                            <ApproverAddress variant="body2">
                              <FontAwesomeIcon icon={faEnvelope} />
                              &nbsp;
                              {approver.email}
                            </ApproverAddress>
                          </a> */}
                        </Grid>
                      </Tooltip>
                    ))}
                  </ApproversContainer>
                </SimplePopover>
              </Grid>
            )}
          </ApproversContainer>
        </TableCell>
        {/* End of expense report Approvers */}
        {/* Client Approvers */}
        {item.projectClientApprovers.length > 0 ? (
          item.projectClientApprovers.slice(0, 1).map((project, idx) => (
            <React.Fragment key={idx}>
              <TableCell>
                <Tooltip
                  title={`${project.project?.name} , From ${
                    CustomDate.ToDate(project.start)
                      ? intlFormatDateShort(CustomDate.ToDate(project.start) as Date)
                      : "<NO START DATE>"
                  } To ${
                    CustomDate.ToDate(project.end)
                      ? intlFormatDateShort(CustomDate.ToDate(project.end) as Date)
                      : "<NO END DATE>"
                  }`}
                >
                  <Grid>
                    <Typography>{project.project?.name}</Typography>
                    <Typography
                      style={{ color: theme.palette.text.disabled }}
                      variant="body2"
                    >{`From ${
                      CustomDate.ToDate(project.start)
                        ? intlFormatDateShort(CustomDate.ToDate(project.start) as Date)
                        : "<NO START DATE>"
                    }`}</Typography>
                    {CustomDate.ToDate(project.end) && (
                      <Typography
                        style={{ color: theme.palette.text.disabled }}
                        variant="body2"
                      >
                        {`To ${intlFormatDateShort(CustomDate.ToDate(project.end) as Date)}`}
                      </Typography>
                    )}
                  </Grid>
                </Tooltip>
              </TableCell>

              <TableCell>
                <ApproversContainer>
                  {filterclientApprover(project.clientApprovers)
                    .slice(0, 3)
                    .map((approver, idx) => (
                      <Tooltip
                        key={idx}
                        title={`${approver.user?.name} , From ${intlFormatDateShort(approver.startDate)} to ${
                          approver.endDate ? intlFormatDateShort(approver.endDate) : "==no end date=="
                        }`}
                      >
                        <Grid>
                          <Typography>{approver.user?.name}</Typography>
                          <a href={`mailto:${approver.user?.email}`}>
                            <ApproverAddress variant="body2">
                              <FontAwesomeIcon icon={faEnvelope} />
                              &nbsp;
                              {approver.user?.email}
                            </ApproverAddress>
                          </a>
                        </Grid>
                      </Tooltip>
                    ))}
                  {filterclientApprover(project.clientApprovers).length > 3 && (
                    <Grid
                      style={{
                        marginLeft: "auto",
                        width: "min-content",
                      }}
                    >
                      <SimplePopover
                        buttonType="icon"
                        buttonContent={<FontAwesomeIcon icon={faEllipsisH} />}
                      >
                        <ApproversContainer style={{ padding: "15px" }}>
                          {filterclientApprover(project.clientApprovers)
                            .slice(3)
                            .map((approver, idx) => (
                              <Grid key={idx}>
                                <Typography>{approver.user?.name}</Typography>
                                <a href={`mailto:${approver.user?.email}`}>
                                  <ApproverAddress variant="body2">
                                    <FontAwesomeIcon icon={faEnvelope} />
                                    &nbsp;
                                    {approver.user?.email}
                                  </ApproverAddress>
                                </a>
                              </Grid>
                            ))}
                        </ApproversContainer>
                      </SimplePopover>
                    </Grid>
                  )}
                </ApproversContainer>
              </TableCell>
            </React.Fragment>
          ))
        ) : (
          <>
            <TableCell></TableCell>
            <TableCell></TableCell>
          </>
        )}
        {/* End ofClient Approvers */}
      </TableRow>
      {item.projectClientApprovers.length > 1 &&
        item.projectClientApprovers.slice(1).map((project, idx) => (
          <TableRow key={idx}>
            <TableCell>
              <Tooltip
                title={`${project.project?.name} , From ${
                  CustomDate.ToDate(project.start)
                    ? intlFormatDateShort(CustomDate.ToDate(project.start) as Date)
                    : "<NO START DATE>"
                } To ${
                  CustomDate.ToDate(project.end)
                    ? intlFormatDateShort(CustomDate.ToDate(project.end) as Date)
                    : "<NO END DATE>"
                }`}
              >
                <Grid>
                  <Typography>{project.project?.name}</Typography>
                  <Typography
                    style={{ color: theme.palette.text.disabled }}
                    variant="body2"
                  >{`From ${
                    CustomDate.ToDate(project.start)
                      ? intlFormatDateShort(CustomDate.ToDate(project.start) as Date)
                      : "<NO START DATE>"
                  }`}</Typography>
                  {CustomDate.ToDate(project.end) && (
                    <Typography
                      style={{ color: theme.palette.text.disabled }}
                      variant="body2"
                    >
                      {`To ${intlFormatDateShort(CustomDate.ToDate(project.end) as Date)}`}
                    </Typography>
                  )}
                </Grid>
              </Tooltip>
            </TableCell>

            <TableCell>
              <ApproversContainer>
                {filterclientApprover(project.clientApprovers)
                  .slice(0, 3)
                  .map((approver, idx) => (
                    <Tooltip
                      key={idx}
                      title={`${approver.user?.name} , From ${intlFormatDateShort(approver.startDate)} to ${
                        approver.endDate ? intlFormatDateShort(approver.endDate) : "==no end date=="
                      }`}
                    >
                      <Grid>
                        <Typography>{approver.user?.name}</Typography>
                        <a href={`mailto:${approver.user?.email}`}>
                          <ApproverAddress variant="body2">
                            <FontAwesomeIcon icon={faEnvelope} />
                            &nbsp;
                            {approver.user?.email}
                          </ApproverAddress>
                        </a>
                      </Grid>
                    </Tooltip>
                  ))}
                {filterclientApprover(project.clientApprovers).length > 3 && (
                  <Grid
                    style={{
                      marginLeft: "auto",
                      width: "min-content",
                    }}
                  >
                    <SimplePopover
                      buttonType="icon"
                      buttonContent={<FontAwesomeIcon icon={faEllipsisH} />}
                    >
                      <ApproversContainer style={{ padding: "15px" }}>
                        {filterclientApprover(project.clientApprovers)
                          .slice(3)
                          .map((approver, idx) => (
                            <Grid key={idx}>
                              <Typography>{approver.user?.name}</Typography>
                              <a href={`mailto:${approver.user?.email}`}>
                                <ApproverAddress variant="body2">
                                  <FontAwesomeIcon icon={faEnvelope} />
                                  &nbsp;
                                  {approver.user?.email}
                                </ApproverAddress>
                              </a>
                            </Grid>
                          ))}
                      </ApproversContainer>
                    </SimplePopover>
                  </Grid>
                )}
              </ApproversContainer>
            </TableCell>
          </TableRow>
        ))}
    </>
  );
};
export default TnAApproverRow;
